import StandardChartEditor from "features/chartLibrary/components/StandardChartEditor";
import AnalyticsChartStatCard, {
  IAnalyticsChartStatCardProps,
} from "./AnalyticsChartStatCard";
import { useCallback, useMemo, useState } from "react";
import { useStandardChart } from "features/chartLibrary/providers/StandardChartsProvider";
import Loading from "Components/Loading/Loading";
import { Alert } from "@mui/material";
import { ApiChartAttribute, ApiChartType } from "@incendium/api";

interface IStandardAnalyticsChartStatCardProps
  extends IAnalyticsChartStatCardProps {
  chartKey: string;
  /** Page-level filters applied on top of saved chart attributes. Not editable in the chart editor. */
  staticAttributes?: ApiChartAttribute[];
}

function mergeChartAttributes(
  staticAttributes: ApiChartAttribute[] | undefined,
  userAttributes: ApiChartAttribute[] | undefined,
) {
  return [...(staticAttributes || []), ...(userAttributes || [])];
}

function StandardAnalyticsChartStatCard({
  chartKey,
  staticAttributes,
  ...props
}: IStandardAnalyticsChartStatCardProps) {
  const {
    chart: loadedChart,
    loading,
    refresh,
    clearChart,
  } = useStandardChart(chartKey);
  const [standardChartEditorOpen, setStandardChartEditorOpen] = useState(false);

  const onStandardChartEdit = useCallback(async () => {
    setStandardChartEditorOpen(true);
  }, []);

  const { chart, editorChart } = useMemo(() => {
    const userAttributes =
      loadedChart?.attributes ?? props.chart.attributes ?? [];
    const merged = {
      ...props.chart,
      ...(loadedChart || {}),
      chartKey,
      type: ApiChartType.STAT_CARD,
      attributes: mergeChartAttributes(staticAttributes, userAttributes),
    };

    if (props.chart.dateDimension !== undefined) {
      merged.dateDimension = props.chart.dateDimension;
    }

    return {
      chart: merged,
      editorChart: {
        ...merged,
        attributes: userAttributes,
      },
    };
  }, [loadedChart, props.chart, chartKey, staticAttributes]);

  if (loading) {
    return <Loading />;
  }

  if (!chartKey) {
    return (
      <>
        <Alert severity="error">Chart key is required</Alert>
      </>
    );
  }

  return (
    <>
      <AnalyticsChartStatCard
        {...props}
        chart={chart}
        onEdit={onStandardChartEdit}
      />
      {!props.noToolbar && (
        <StandardChartEditor
          open={standardChartEditorOpen}
          onClose={() => setStandardChartEditorOpen(false)}
          chart={editorChart}
          onSaved={() => {
            refresh();
            setStandardChartEditorOpen(false);
          }}
          onDeleted={() => {
            clearChart();
          }}
        />
      )}
    </>
  );
}

export default StandardAnalyticsChartStatCard;
